import { useEffect, useState, type JSX } from 'react';
import { KBIcon } from '@/components/kbIcons';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { Lang } from '@/data/types';
import { friendlyError } from '@/lib/friendlyError';
import { ParallelIcon, HubSpotIcon, DriveGlyph } from '@/views/cases/ui';
import { actesUpdateDossier } from '@/integrations/actesApi';
import { actesEn } from '@/lib/actesCountry';

/**
 * LES TROIS PORTES D'UN DOSSIER — Drive, Parallel, HubSpot.
 *
 * They are always all three, which is the point. Before, a link that was not
 * set simply did not appear, so the only way to add one was to open the edit
 * form and hunt for the right field — and nobody did, which is why so many
 * dossiers have no Drive folder recorded anywhere.
 *
 * Set: the brand mark in colour, a click opens it.
 * Not set: dashed and grey with a small +, a click asks for the address.
 *
 * Saving does NOT open the link. You were recording where something lives, not
 * asking to go there — and being thrown into a new tab for it was the reason
 * people stopped filling them in. The chip lights up on the spot instead.
 */
type Kind = 'drive' | 'parallel' | 'hubspot';
const FIELD: Record<Kind, 'drive_url' | 'parallel_url' | 'hubspot_url'> = {
  drive: 'drive_url', parallel: 'parallel_url', hubspot: 'hubspot_url',
};
const PLACEHOLDER: Record<Kind, string> = {
  drive: 'https://drive.google.com/…',
  parallel: 'https://app.parallel.so/…',
  hubspot: 'https://app.hubspot.com/…',
};

export function LinkChips({ lang, dossierId, drive, parallel, hubspot, canWrite }: {
  lang: Lang; dossierId: string;
  drive: string | null; parallel: string | null; hubspot: string | null;
  canWrite: boolean;
}) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Kind | null>(null);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  /* Lit the moment the save lands, before the refetch comes back. The server
     value is authoritative: when it changes, the optimistic one is dropped. */
  const [saved, setSaved] = useState<Partial<Record<Kind, string>>>({});
  const [flash, setFlash] = useState<Kind | null>(null);
  useEffect(() => { setSaved({}); }, [drive, parallel, hubspot]);

  const urlOf = (k: Kind) => saved[k] ?? (k === 'drive' ? drive : k === 'parallel' ? parallel : hubspot);

  const save = async () => {
    const v = value.trim();
    if (!editing || !v) return;
    setBusy(true); setErr('');
    try {
      await actesUpdateDossier(dossierId, { [FIELD[editing]]: v });
      setSaved((p) => ({ ...p, [editing]: v }));
      setFlash(editing);
      window.setTimeout(() => setFlash(null), 500);
      setEditing(null);
      await qc.invalidateQueries({ queryKey: ['actes'] });
    } catch (e) { setErr(friendlyError(e, lang)); }
    setBusy(false);
  };

  const CHIPS: { k: Kind; icon: JSX.Element; name: string }[] = [
    { k: 'drive', icon: <DriveGlyph />, name: 'Drive' },
    { k: 'parallel', icon: <ParallelIcon size={17} />, name: 'Parallel' },
    { k: 'hubspot', icon: <HubSpotIcon size={17} />, name: 'HubSpot' },
  ];

  return (
    <>
      <span className="actes-chips">
        {CHIPS.map(({ k, icon, name }) => {
          const url = urlOf(k);
          return (
            <button key={k} type="button"
              className={`actes-chip ${url ? 'set' : 'unset'} ${flash === k ? 'pop' : ''}`}
              disabled={!url && !canWrite}
              title={url ? T(`Ouvrir ${name}`, `Open ${name}`) : T(`Associer ${name}`, `Link ${name}`)}
              aria-label={url ? T(`Ouvrir ${name}`, `Open ${name}`) : T(`Associer ${name}`, `Link ${name}`)}
              onClick={() => {
                if (url) { window.open(url, '_blank', 'noopener,noreferrer'); return; }
                setValue(''); setErr(''); setEditing(k);
              }}>
              {icon}
              {!url && <em>+</em>}
            </button>
          );
        })}
      </span>

      {editing && createPortal(
        <div className="scrim actes-skin" onClick={() => setEditing(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className="modal-h">
              <div>
                <div className="eyebrow">{CHIPS.find((c) => c.k === editing)!.name}</div>
                <h3>{T(`Associer ${CHIPS.find((c) => c.k === editing)!.name}`, `Link ${CHIPS.find((c) => c.k === editing)!.name}`)}</h3>
              </div>
              <button className="rb x" onClick={() => setEditing(null)} aria-label={T('Fermer', 'Close')}>
                <KBIcon name="x" size={14} />
              </button>
            </div>
            <div className="modal-b">
              <p className="muted-note">
                {T('Collez l’adresse. Elle est enregistrée ici — le lien ne s’ouvre pas.',
                   'Paste the address. It is recorded here — the link is not opened.')}
              </p>
              <label className="fl">
                <span>{T('Adresse', 'Address')}</span>
                <input autoFocus type="url" value={value} placeholder={PLACEHOLDER[editing]}
                  onChange={(e) => setValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void save(); }} />
              </label>
              {err && <p className="muted-note ni-err">⚠ {err}</p>}
            </div>
            <div className="modal-f">
              <span className="spacer" />
              <button className="btn" onClick={() => setEditing(null)}>{T('Annuler', 'Cancel')}</button>
              <button className="btn btn-p" disabled={busy || !value.trim()} onClick={() => void save()}>
                {busy ? '…' : T('Enregistrer', 'Save')}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
